import type { LatLon } from '../geo';
import { distanceNm } from '../geo';
import type { Bathymetry, MapBounds } from './bathymetry';

/**
 * Wegsuche durch befahrbares Wasser.
 *
 * Ohne sie faehrt jedes Schiff die Luftlinie — und die fuehrt im Bermuda-Dreieck
 * regelmaessig quer ueber die Grosse Bahama-Bank, wo stellenweise drei Meter
 * Wasser stehen. Bis hierher wurde davor nur gewarnt.
 *
 * Der Ablauf hat drei Schritte:
 *  1. Aus dem Hoehenraster ein grobes Befahrbarkeitsraster je Tiefgang bauen.
 *  2. Darauf mit A* einen Weg suchen.
 *  3. Den Weg per Sichtlinie glaetten, damit daraus ein Kurs wird, den ein
 *     Navigator so absetzen wuerde, und keine Rasterppe.
 *
 * ## Warum die gezeichnete Kueste mit ins Raster gehoert
 *
 * Bis M8 entschied allein das Hoehenraster, wo Land ist — mit einer Bogenminute
 * Rasterweite, also rund einer Seemeile. Gezeichnet wird die Kueste dagegen aus
 * Natural-Earth-Vektoren, die um Groessenordnungen feiner sind. Beides
 * auseinanderzuhalten war ein Fehler mit sichtbarer Folge: **An 17 Prozent der
 * gezeichneten Kuestenpunkte meldet das Hoehenraster mehr als sechs Meter
 * Wasser**, an gut zehn Prozent sogar mehr als zwanzig. Bei steilen Kuesten und
 * schmalen Cays faellt der Grund innerhalb weniger hundert Meter ab; der
 * abgetastete Zellmittelpunkt liegt dann in tiefem Wasser, waehrend zweihundert
 * Meter weiter eine Insel gezeichnet ist. Das Schiff fuhr also voellig
 * regelkonform ueber Land.
 *
 * Seither ist die gezeichnete Kueste selbst die Sperre. Damit kann eine Route
 * sie nicht mehr kreuzen — **durch Konstruktion**, nicht durch Nachpruefen.
 * Der Preis ist gemessen und klein: Von 290.700 Navigationszellen beruehrt die
 * Kueste 4.476, wovon nur 270 vorher befahrbar waren — ein Neuntel Prozent des
 * Seeraums.
 */

export interface NavGrid {
  readonly width: number;
  readonly height: number;
  readonly bounds: MapBounds;
  /** 1 = befahrbar, 0 = zu flach oder Land. */
  readonly passable: Uint8Array;
  /** Wie viele Rasterpunkte der Bathymetrie eine Zelle zusammenfasst. */
  readonly step: number;
}

export interface Route {
  readonly waypoints: readonly LatLon[];
  readonly lengthNm: number;
  /** Verhaeltnis zur Luftlinie. 1 = direkt, 1.4 = 40 Prozent Umweg. */
  readonly detourFactor: number;
}

/**
 * Zellgroesse des Navigationsrasters, in Rasterpunkten der Bathymetrie.
 *
 * Zwei Bogenminuten je Zelle sind fuer die Seeroute fein genug und halten die
 * Suche bei rund 290.000 Zellen — das laeuft in Millisekunden statt Sekunden.
 */
const DEFAULT_STEP = 2;

/** Ein Kuestenzug als Folge von [lon, lat]-Paaren — genau die Form, in der
 * `data/map/coastline.json` sie liefert. */
export type CoastStrand = readonly (readonly [number, number])[];

export interface NavGridOptions {
  /**
   * Die gezeichneten Kuestenzuege. Werden als unbefahrbar eingebrannt.
   *
   * Ohne sie ist das Raster nur so genau wie das Hoehenraster — siehe der
   * Modulkommentar oben.
   */
  readonly coastline?: readonly CoastStrand[];
  /**
   * Sicherheitsabstand zur Kueste in Zellen.
   *
   * Eine Zelle sind zwei Bogenminuten, also gut zwei Seemeilen. Ein Schiff
   * faehrt nicht auf Tuchfuehlung an einem Riff entlang, und eine nur eine
   * Zelle dicke Sperre laesst sich von der geglaetteten Kurslinie zu leicht
   * anschneiden.
   */
  readonly coastMarginCells?: number;
}

const DEFAULT_COAST_MARGIN = 1;

/**
 * Baut das Befahrbarkeitsraster.
 *
 * Eine Zelle gilt nur dann als befahrbar, wenn die **flachste** Stelle darin
 * genug Wasser hat. Das ist bewusst vorsichtig: Lieber ein Umweg zu viel als
 * ein Schiff auf einer Sandbank. Danach werden die gezeichneten Kuestenzuege
 * daruebergelegt und gesperrt.
 */
export function buildNavGrid(
  bathymetry: Bathymetry,
  draughtMetres: number,
  step: number = DEFAULT_STEP,
  options: NavGridOptions = {},
): NavGrid {
  const width = Math.floor(bathymetry.width / step);
  const height = Math.floor(bathymetry.height / step);
  const passable = new Uint8Array(width * height);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let shallowest = Number.POSITIVE_INFINITY;

      for (let dy = 0; dy < step; dy++) {
        for (let dx = 0; dx < step; dx++) {
          const depth = -bathymetry.elevationAtIndex(x * step + dx, y * step + dy);
          if (depth < shallowest) shallowest = depth;
        }
      }

      passable[y * width + x] = shallowest > draughtMetres ? 1 : 0;
    }
  }

  const grid: NavGrid = { width, height, bounds: bathymetry.meta.bounds, passable, step };

  if (options.coastline !== undefined && options.coastline.length > 0) {
    burnCoastline(grid, options.coastline, options.coastMarginCells ?? DEFAULT_COAST_MARGIN);
  }

  return grid;
}

/**
 * Sperrt jede Zelle, die ein gezeichneter Kuestenzug beruehrt, samt Umkreis.
 *
 * Gesammelt wird zuerst, gesperrt danach: Wuerde direkt beim Durchlaufen
 * gesperrt und gleichzeitig der Umkreis mitgezogen, wuechse die Sperre mit
 * jedem weiteren Zug weiter ins Wasser hinein.
 */
function burnCoastline(grid: NavGrid, coastline: readonly CoastStrand[], margin: number): void {
  const touched = new Set<number>();

  const mark = (x: number, y: number): void => {
    if (x < 0 || y < 0 || x >= grid.width || y >= grid.height) return;
    touched.add(y * grid.width + x);
  };

  for (const strand of coastline) {
    let previous: { x: number; y: number } | null = null;

    for (const point of strand) {
      const cell = cellOf(grid, { lon: point[0], lat: point[1] });
      if (previous !== null) traceLine(previous, cell, mark);
      mark(cell.x, cell.y);
      previous = cell;
    }
  }

  for (const key of touched) {
    const cx = key % grid.width;
    const cy = (key / grid.width) | 0;

    for (let dy = -margin; dy <= margin; dy++) {
      for (let dx = -margin; dx <= margin; dx++) {
        const x = cx + dx;
        const y = cy + dy;
        if (x < 0 || y < 0 || x >= grid.width || y >= grid.height) continue;
        grid.passable[y * grid.width + x] = 0;
      }
    }
  }
}

/** Alle Zellen zwischen zwei Rasterpunkten, ohne Luecken. */
function traceLine(
  a: { x: number; y: number },
  b: { x: number; y: number },
  visit: (x: number, y: number) => void,
): void {
  const steps = Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y));
  for (let i = 0; i <= steps; i++) {
    const t = steps === 0 ? 0 : i / steps;
    visit(Math.round(a.x + (b.x - a.x) * t), Math.round(a.y + (b.y - a.y) * t));
  }
}

function cellOf(grid: NavGrid, p: LatLon): { x: number; y: number } {
  const { north, south, west, east } = grid.bounds;
  const fx = ((p.lon - west) / (east - west)) * (grid.width - 1);
  const fy = ((north - p.lat) / (north - south)) * (grid.height - 1);
  return {
    x: Math.max(0, Math.min(grid.width - 1, Math.round(fx))),
    y: Math.max(0, Math.min(grid.height - 1, Math.round(fy))),
  };
}

function positionOf(grid: NavGrid, x: number, y: number): LatLon {
  const { north, south, west, east } = grid.bounds;
  return {
    lon: west + (x / (grid.width - 1)) * (east - west),
    lat: north - (y / (grid.height - 1)) * (north - south),
  };
}

function isPassable(grid: NavGrid, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= grid.width || y >= grid.height) return false;
  return grid.passable[y * grid.width + x] === 1;
}

/**
 * Wie weit die Suche nach einem befahrbaren Anschlusspunkt hoechstens reicht.
 *
 * Frueher dreissig Zellen — gut sechzig Seemeilen, und weil der Schenkel vom
 * echten Endpunkt dorthin ungeprueft bleibt, war das eine sechzig Seemeilen
 * lange Lizenz zum Ueberfahren von Land. Acht Zellen sind rund sechzehn
 * Seemeilen und damit ein Anlaufweg, den man erzaehlen kann.
 */
const ANCHOR_SEARCH_RADIUS = 8;

/**
 * Sucht die naechste befahrbare Zelle in wachsenden Ringen.
 * Noetig, weil Start und Ziel auf flachem Wasser liegen koennen — die Station
 * steht auf 18 m Bankwasser, ein Wrack kann in einer Untiefe liegen.
 */
function nearestPassable(
  grid: NavGrid,
  cell: { x: number; y: number },
  maxRadius = ANCHOR_SEARCH_RADIUS,
): { x: number; y: number } | null {
  if (isPassable(grid, cell.x, cell.y)) return cell;

  for (let radius = 1; radius <= maxRadius; radius++) {
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        // Nur den Rand des Rings pruefen, das Innere war schon dran.
        if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) continue;
        const x = cell.x + dx;
        const y = cell.y + dy;
        if (isPassable(grid, x, y)) return { x, y };
      }
    }
  }

  return null;
}

/** Kleiner binaerer Heap — ohne ihn dominiert das Suchen des Minimums die Laufzeit. */
class MinHeap {
  private readonly items: { key: number; priority: number }[] = [];

  get size(): number {
    return this.items.length;
  }

  push(key: number, priority: number): void {
    this.items.push({ key, priority });
    let i = this.items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.items[parent]!.priority <= this.items[i]!.priority) break;
      [this.items[parent], this.items[i]] = [this.items[i]!, this.items[parent]!];
      i = parent;
    }
  }

  pop(): number | undefined {
    const top = this.items[0];
    if (top === undefined) return undefined;

    const last = this.items.pop() as { key: number; priority: number };
    if (this.items.length > 0) {
      this.items[0] = last;
      let i = 0;
      for (;;) {
        const left = i * 2 + 1;
        const right = left + 1;
        let smallest = i;
        if (left < this.items.length && this.items[left]!.priority < this.items[smallest]!.priority) {
          smallest = left;
        }
        if (right < this.items.length && this.items[right]!.priority < this.items[smallest]!.priority) {
          smallest = right;
        }
        if (smallest === i) break;
        [this.items[smallest], this.items[i]] = [this.items[i]!, this.items[smallest]!];
        i = smallest;
      }
    }

    return top.key;
  }
}

const NEIGHBOURS: readonly (readonly [number, number])[] = [
  [1, 0], [-1, 0], [0, 1], [0, -1],
  [1, 1], [1, -1], [-1, 1], [-1, -1],
];

/**
 * Liegt zwischen zwei Zellen eine durchgehend befahrbare Sichtlinie?
 *
 * Bewusst **nicht** das gewoehnliche Bresenham. Das setzt bei einem
 * Diagonalschritt beide Achsen zugleich weiter und ueberspringt dabei die zwei
 * Zellen, an denen es vorbeischneidet. Genau durch diesen Spalt schluepft eine
 * ein Zelle dicke Sperre — und die eingebrannte Kuestenlinie ist so eine.
 * Deshalb wird bei jedem Diagonalschritt zusaetzlich geprueft, ob wenigstens
 * eine der beiden gestreiften Zellen frei ist.
 */
function hasLineOfSight(grid: NavGrid, a: { x: number; y: number }, b: { x: number; y: number }): boolean {
  let x = a.x;
  let y = a.y;
  const dx = Math.abs(b.x - a.x);
  const dy = Math.abs(b.y - a.y);
  const sx = a.x < b.x ? 1 : -1;
  const sy = a.y < b.y ? 1 : -1;
  let err = dx - dy;

  for (;;) {
    if (!isPassable(grid, x, y)) return false;
    if (x === b.x && y === b.y) return true;

    const e2 = err * 2;
    const stepX = e2 > -dy;
    const stepY = e2 < dx;

    // Diagonalschritt: Der Kurs streift die beiden Nachbarzellen. Sind beide
    // gesperrt, fuehrt er durch eine Landenge — das ist kein Seeweg.
    if (stepX && stepY && !isPassable(grid, x + sx, y) && !isPassable(grid, x, y + sy)) {
      return false;
    }

    if (stepX) {
      err -= dy;
      x += sx;
    }
    if (stepY) {
      err += dx;
      y += sy;
    }
  }
}

/**
 * Zieht den Rasterweg gerade.
 *
 * A* liefert einen Weg entlang der Rasterkanten — im Ergebnis eine Treppe aus
 * 45-Grad-Stufen. Ein Navigator faehrt stattdessen die laengste gerade Strecke,
 * die frei ist, und schlaegt dann einen neuen Kurs an. Genau das macht diese
 * Glaettung.
 */
function smooth(grid: NavGrid, cells: { x: number; y: number }[]): { x: number; y: number }[] {
  if (cells.length <= 2) return cells;

  const result: { x: number; y: number }[] = [cells[0] as { x: number; y: number }];
  let anchor = 0;

  while (anchor < cells.length - 1) {
    let furthest = anchor + 1;

    for (let candidate = cells.length - 1; candidate > anchor + 1; candidate--) {
      if (hasLineOfSight(grid, cells[anchor] as { x: number; y: number }, cells[candidate] as { x: number; y: number })) {
        furthest = candidate;
        break;
      }
    }

    result.push(cells[furthest] as { x: number; y: number });
    anchor = furthest;
  }

  return result;
}

export interface RouteOptions {
  /** Obergrenze der untersuchten Zellen, damit eine aussichtslose Suche endet. */
  readonly maxExplored?: number;
}

/**
 * Sucht einen befahrbaren Kurs. Liefert `null`, wenn keiner existiert.
 */
export function findRoute(
  grid: NavGrid,
  from: LatLon,
  to: LatLon,
  options: RouteOptions = {},
): Route | null {
  const maxExplored = options.maxExplored ?? 400_000;

  const start = nearestPassable(grid, cellOf(grid, from));
  const goal = nearestPassable(grid, cellOf(grid, to));
  if (start === null || goal === null) return null;

  const size = grid.width * grid.height;
  const startKey = start.y * grid.width + start.x;
  const goalKey = goal.y * grid.width + goal.x;

  const cost = new Float32Array(size).fill(Number.POSITIVE_INFINITY);
  const cameFrom = new Int32Array(size).fill(-1);
  const closed = new Uint8Array(size);

  const heuristic = (key: number): number => {
    const x = key % grid.width;
    const y = (key / grid.width) | 0;
    return Math.hypot(x - goal.x, y - goal.y);
  };

  cost[startKey] = 0;
  const open = new MinHeap();
  open.push(startKey, heuristic(startKey));

  let explored = 0;

  while (open.size > 0) {
    const current = open.pop() as number;
    if (closed[current] === 1) continue;
    closed[current] = 1;

    if (current === goalKey) break;
    if (++explored > maxExplored) return null;

    const cx = current % grid.width;
    const cy = (current / grid.width) | 0;

    for (const [dx, dy] of NEIGHBOURS) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (!isPassable(grid, nx, ny)) continue;

      // Kein Schnitt durch die Ecke: Ein Diagonalschritt ist nur erlaubt, wenn
      // auch einer der beiden orthogonalen Nachbarn frei ist. Sonst faehrt der
      // Kurs durch eine Landenge, die auf der Karte geschlossen aussieht.
      if (dx !== 0 && dy !== 0 && !isPassable(grid, cx + dx, cy) && !isPassable(grid, cx, cy + dy)) {
        continue;
      }

      const neighbour = ny * grid.width + nx;
      if (closed[neighbour] === 1) continue;

      // Diagonalen sind laenger — ohne diesen Faktor entstehen Zickzackkurse.
      const stepCost = dx !== 0 && dy !== 0 ? Math.SQRT2 : 1;
      const tentative = (cost[current] as number) + stepCost;

      if (tentative < (cost[neighbour] as number)) {
        cost[neighbour] = tentative;
        cameFrom[neighbour] = current;
        open.push(neighbour, tentative + heuristic(neighbour));
      }
    }
  }

  if (closed[goalKey] !== 1) return null;

  // Weg zurueckverfolgen.
  const cells: { x: number; y: number }[] = [];
  for (let key = goalKey; key !== -1; key = cameFrom[key] as number) {
    cells.push({ x: key % grid.width, y: (key / grid.width) | 0 });
    if (key === startKey) break;
  }
  cells.reverse();

  const smoothed = smooth(grid, cells);

  /*
   * Die tatsaechlichen Endpunkte anfuegen: Start und Ziel liegen womoeglich in
   * flachem Wasser oder dicht an einer Kueste, die das Raster jetzt sperrt —
   * die Station tut genau das.
   *
   * Diese beiden Schenkel koennen als einzige nicht geprueft werden, denn ohne
   * sie kaeme das Schiff nie an. Frueher war das ein stiller Fehler: Die Suche
   * nach der naechsten befahrbaren Zelle reichte bis dreissig Zellen weit, gut
   * sechzig Seemeilen, und der Verbindungsschenkel lief quer ueber alles, was
   * dazwischen lag. Deshalb ist ihre Reichweite jetzt eng begrenzt
   * (ANCHOR_SEARCH_RADIUS): Was uebrig bleibt, ist ein Anlaufweg von wenigen
   * Seemeilen — die Strecke, die ein Beiboot ohnehin macht.
   */
  const inner = smoothed.map((c) => positionOf(grid, c.x, c.y));
  const waypoints: LatLon[] = [from, ...inner, to];

  const deduped = waypoints.filter(
    (p, i) => i === 0 || distanceNm(p, waypoints[i - 1] as LatLon) > 0.5,
  );

  let length = 0;
  for (let i = 1; i < deduped.length; i++) {
    length += distanceNm(deduped[i - 1] as LatLon, deduped[i] as LatLon);
  }

  const direct = distanceNm(from, to);
  return {
    waypoints: deduped,
    lengthNm: length,
    detourFactor: direct > 0 ? length / direct : 1,
  };
}

/** Ersatzroute, wenn keine Wegsuche moeglich ist: die Luftlinie. */
export function directRoute(from: LatLon, to: LatLon): Route {
  return {
    waypoints: [from, to],
    lengthNm: distanceNm(from, to),
    detourFactor: 1,
  };
}
